import CheckCircleIcon from '@material-ui/icons/CheckCircleOutlined'
import { JIRA_SUPPORT_TICKET_LINK } from 'constants/links'
import { ComponentVersions_3_0 } from 'constants/uxVersions'
import { Struct } from 'contexts/structs/types/types'
import { useUserActionsContext } from 'contexts/userActions/useUserActionsContext'
import { authSelectors } from 'ducks/auth'
import { featuresActions, featuresSelectors } from 'ducks/features'
import { orgsActions, orgSelectors } from 'ducks/orgs'
import { HelpLink } from 'elements/link/HelpLink'
import { useTranslatableText } from 'hooks/translation/useTranslatableText'
import { useTranslateOrTranslatableText } from 'hooks/translation/useTranslateOrTranslatableText'
import {
  Alert,
  Button,
  Chip,
  ChipProps,
  ComponentVersionsInherit,
  Dialog,
  DialogActions,
  DialogCloseButton,
  DialogContent,
  DialogTitle,
  styled,
  Switch,
  Tooltip,
  Typography,
} from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'
import { OrgFeatureSettings } from 'types/orgs'
import {
  FeatureFlagOptState,
  FeatureFlagStateMap,
  FeatureFlagType,
  getFeatureFlagUserActionKey,
  useFeatureFlagStructsWithState,
} from 'util/featureFlags'
import { doNotTranslate } from 'util/misc'
import useTranslateParse from 'util/useTranslateParse'

export const FeaturesDialog = () => {
  const { states } = useFeatureFlagStructsWithState()
  const translatableText = useTranslatableText()
  const isStaff = useSelector(authSelectors.getIsStaff)
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const open = useSelector(featuresSelectors.getFeaturesDialogOpen)
  const dispatch = useDispatch()

  const onClose = () => {
    dispatch(featuresActions.setFeaturesDialogOpen(false))
  }

  const sortedOptionalUser = useMemo(() => {
    const optional = (states.enabled_user_optout || [])
      .concat(states.disabled_user_optin || [])
      .concat(states.disabled_user_apply || [])
    return optional.sort((a, b) =>
      translatableText(a.data.description).localeCompare(translatableText(b.data.description))
    )
  }, [states])

  const sortedOptionalOrg = useMemo(() => {
    if (!isAdmin) return []
    const optional = (states.enabled_org_optout || [])
      .concat(states.disabled_org_optin || [])
      .concat(states.disabled_org_apply || [])
    return optional.sort((a, b) =>
      translatableText(a.data.description).localeCompare(translatableText(b.data.description))
    )
  }, [states, isAdmin])

  const showUser = !!sortedOptionalUser.length
  const showOrg = !!sortedOptionalOrg.length
  const showHeadings = (showUser && showOrg) || isStaff

  const translate = useTranslate()
  const translateParse = useTranslateParse()
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <span>{translate('OpenSolar Beta Features')}</span>
          <DialogCloseButton onClick={onClose} />
        </DialogTitle>
        <DialogContent style={{ padding: 24 }}>
          <ElementsContainer>
            <Typography textVariant="body1">
              {translate(
                'Explore upcoming features before they go live! You can enable beta features for yourself or your organisation, and join waitlists to be first in line when new capabilities roll out.'
              )}
            </Typography>
            {showUser && (
              <>
                {showHeadings && <Heading title={translate('User Features')} />}
                {sortedOptionalUser.map((struct) => {
                  return <FeatureFlagRowUser key={struct.key} struct={struct} state={getState(states, struct)} />
                })}
              </>
            )}
            {showOrg && (
              <>
                {showHeadings && <Heading title={translate('Org Features')} />}

                {sortedOptionalOrg.map((struct) => {
                  return <FeatureFlagRowOrg key={struct.key} struct={struct} state={getState(states, struct)} />
                })}
              </>
            )}

            {isStaff && (
              <>
                {showHeadings && (
                  <>
                    <Typography textVariant="subtitle1">
                      {doNotTranslate('Currently Enabled/Disabled Features')}
                    </Typography>
                    <Alert severity="info">
                      <>
                        {doNotTranslate(
                          'This section only shows for OS Staff, and only shows feature flags defined internally in the Structs system (in Control > Staff).'
                        )}
                      </>
                    </Alert>
                  </>
                )}
                <FeatureFlagStateChips structs={states.enabled} title={doNotTranslate('Enabled')} color="success" />
                <FeatureFlagStateChips structs={states.disabled} title={doNotTranslate('Disabled')} color="error" />
                <FeatureFlagStateChips
                  structs={states.enabled_org_optout}
                  title={doNotTranslate('Enabled Org Opt-out Toggle')}
                  color="info"
                />
                <FeatureFlagStateChips
                  structs={states.enabled_user_optout}
                  title={doNotTranslate('Enabled User Opt-out Toggle')}
                  color="info"
                />
                <FeatureFlagStateChips
                  structs={states.disabled_org_optin}
                  title={doNotTranslate('Disabled Org Opt-in Toggle')}
                  color="warning"
                />
                <FeatureFlagStateChips
                  structs={states.disabled_user_optin}
                  title={doNotTranslate('Disabled User Opt-in Toggle')}
                  color="warning"
                />
                <FeatureFlagStateChips
                  structs={states.disabled_org_apply}
                  title={doNotTranslate('Disabled Org Apply Now Button')}
                  color="warning"
                />
                <FeatureFlagStateChips
                  structs={states.disabled_user_apply}
                  title={doNotTranslate('Disabled User Apply Now Button')}
                  color="warning"
                />
              </>
            )}
          </ElementsContainer>
        </DialogContent>
        <DialogActions>
          <Alert severity="info">
            {translateParse(
              'Noticed something off with a beta feature? <a>Submit a support request<a> and help us improve beta features.',
              {
                a: (label: string) => (
                  <a href={JIRA_SUPPORT_TICKET_LINK} target="_blank" rel="noopener noreferrer">
                    {label}
                  </a>
                ),
              }
            )}
          </Alert>
        </DialogActions>
      </Dialog>
    </ComponentVersionsInherit>
  )
}
const getState = (states: FeatureFlagStateMap, struct: Struct<FeatureFlagType>): FeatureFlagOptState => {
  if (states.enabled_user_optout?.includes(struct)) return 'enabled_user_optout'
  if (states.disabled_user_optin?.includes(struct)) return 'disabled_user_optin'
  if (states.enabled_org_optout?.includes(struct)) return 'enabled_org_optout'
  if (states.disabled_org_optin?.includes(struct)) return 'disabled_org_optin'
  if (states.disabled_user_apply?.includes(struct)) return 'disabled_user_apply'
  if (states.disabled_org_apply?.includes(struct)) return 'disabled_org_apply'
  throw Error('Something went wrong, unknown feature flag state')
}

const ElementsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const FeatureRow = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  alignItems: 'center',
})

const FeatureLabel = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: 4,
  flexGrow: 1,
})

const FeatureFlagRowUser = ({ struct, state }: { struct: Struct<FeatureFlagType>; state: FeatureFlagOptState }) => {
  const userActions = useUserActionsContext()
  const orgId = useSelector(authSelectors.getOrgId)
  const actionName = getFeatureFlagUserActionKey(state, struct.key)

  const didOptInOut = useMemo(() => {
    return userActions.checkAction(actionName)
  }, [state, userActions.actions])

  const enabled = useMemo(() => {
    const default_value = state === 'enabled_user_optout'
    return didOptInOut ? !default_value : default_value
  }, [state, didOptInOut])

  const onToggle = () => {
    if (didOptInOut) userActions.deleteAction(actionName)
    else userActions.recordAction(actionName)
  }

  const onApply = async () => {
    await restClientInstance('CUSTOM_POST', 'custom', {
      url: 'orgs/' + orgId + '/features_settings/' + struct.key + '/join_waitlist/',
    })
  }

  return (
    <FeatureFlagRow
      struct={struct}
      onToggle={onToggle}
      enabled={enabled}
      onApply={state === 'disabled_user_apply' ? onApply : undefined}
    />
  )
}
const restClientInstance = restClient(window.API_ROOT + '/api')

const FeatureFlagRowOrg = ({ struct, state }: { struct: Struct<FeatureFlagType>; state: FeatureFlagOptState }) => {
  const featureSettings = useSelector(orgSelectors.getOrgFeatureSettings)
  const orgId = useSelector(authSelectors.getOrgId)
  const dispatch = useDispatch()

  const thisFeatureSettings = featureSettings?.[struct.key]

  const enabled = useMemo(() => {
    switch (state) {
      case 'enabled_org_optout':
        return !thisFeatureSettings?.optout
      case 'disabled_org_optin':
        return !!thisFeatureSettings?.optin
      default:
        return false
    }
  }, [state, thisFeatureSettings?.optout, thisFeatureSettings?.optin])

  const onToggle = () => {
    const settings: OrgFeatureSettings = {
      ...thisFeatureSettings,
    }
    if (state === 'enabled_org_optout') {
      settings.optout = enabled
    } else {
      settings.optin = !enabled
    }
    dispatch(orgsActions.setOrgFeatureSetting(orgId as number, struct.key, settings))
  }

  const onApply = async () => {
    await restClientInstance('CUSTOM_POST', 'custom', {
      url: 'orgs/' + orgId + '/features_settings/' + struct.key + '/join_waitlist/',
    })
  }

  return (
    <FeatureFlagRow
      struct={struct}
      onToggle={onToggle}
      enabled={enabled}
      onApply={state === 'disabled_org_apply' ? onApply : undefined}
    />
  )
}

const FeatureFlagRow = ({
  struct,
  onToggle,
  enabled,
  onApply,
}: {
  struct: Struct<FeatureFlagType>
  onToggle: () => void
  enabled: boolean
  onApply?: () => Promise<void>
}) => {
  const translate = useTranslateOrTranslatableText()
  const org = useSelector(orgSelectors.getOrg)
  const [isApplying, setIsApplying] = useState(false)
  const [hasApplied, setHasApplied] = useState(false)
  const translatableText = useTranslatableText()

  const moreInfoUrl = translatableText(struct.data.more_info_url)

  const doApply = async () => {
    let applyEmailsOrUrl = struct.data.apply_emails_or_url
    if (!applyEmailsOrUrl) {
      console.log("No apply emails or url found, shouldn't be possible")
      return
    }
    if (applyEmailsOrUrl.startsWith('http://') || applyEmailsOrUrl.startsWith('https://')) {
      window.open(applyEmailsOrUrl, '_blank', 'noopener,noreferrer')
      return
    }

    if (!onApply) return
    setIsApplying(true)
    try {
      await onApply()
      setHasApplied(true)
    } finally {
      setIsApplying(false)
    }
  }

  const renderAction = () => {
    if (!onApply) {
      return <Switch disabled={!org} onChange={onToggle} checked={enabled} />
    } else if (hasApplied) {
      return (
        <>
          <CheckCircleIcon style={{ color: 'green' }} />
          <span style={{ color: 'green' }}>{translate('Joined')}</span>
        </>
      )
    } else {
      return (
        <Button color="primary" variant="contained" onClick={doApply} disabled={isApplying || hasApplied}>
          {translate('Join Waitlist')}
        </Button>
      )
    }
  }

  return (
    <FeatureRow>
      <FeatureLabel>
        <Typography textVariant="body1">{translate(struct.data.description)}</Typography>
        {moreInfoUrl && <HelpLink href={moreInfoUrl} title={false} />}
      </FeatureLabel>
      {renderAction()}
    </FeatureRow>
  )
}

const Heading = ({ title }: { title: string }) => {
  return (
    <div style={{ padding: '30px 20px 0px 20px' }}>
      <Typography textVariant="subtitle2">{title}</Typography>
    </div>
  )
}

const FeatureFlagStateChips = ({
  title,
  structs,
  color,
}: {
  title: string
  structs?: Struct<FeatureFlagType>[]
  color: ChipProps['color']
}) => {
  const translate = useTranslateOrTranslatableText()
  return (
    <div>
      <Typography textVariant="subtitle2">{title}</Typography>
      <ChipWrapper>
        {!structs?.length
          ? doNotTranslate('None')
          : structs.map((struct) => {
              return (
                <Tooltip key={struct.key} title={translate(struct.data.description)} placement="bottom">
                  <Chip key={struct.key} label={struct.key} color={color} size="small" />
                </Tooltip>
              )
            })}
      </ChipWrapper>
    </div>
  )
}

const ChipWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: 4,
  flexWrap: 'wrap',
})
